// Patient Location Service
// Staff-side API for patient location-share links: send the patient an SMS / WhatsApp link, they open it and
// allow browser geolocation, and the coordinates are stored server-side. The popup polls getShare for the result.
// Backend: smartambulancemvpbackend/src/modules/patientLocation (mounted at /api/patient-location-shares).
import { api, handleApiError, handleApiSuccess } from './api.js';

const BASE = '/patient-location-shares';

export const LOCATION_SHARE_CHANNELS = ['SMS', 'WHATSAPP'];

// Statuses after which polling is pointless
export const TERMINAL_SHARE_STATUSES = ['EXPIRED', 'REVOKED'];

const call = async (request) => {
  try {
    return handleApiSuccess(await request()).data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Create a location link and send it to the patient.
 * Without bookingId it is a draft for the booking form: pass the returned share.shareId as
 * `locationShareId` when the booking is created so the backend attaches it.
 * @param {Object} payload
 * @param {string} payload.phone
 * @param {string} [payload.bookingId]
 * @param {string} [payload.patientName]
 * @param {string[]} [payload.channels] - subset of SMS / WHATSAPP (default SMS)
 * @returns {Promise<{share: Object, delivery: Object, reused: boolean, devLink?: string}>}
 *   devLink is only returned by non-production servers (lets you test without a working SMS provider)
 */
export const createLocationShare = (payload) => call(() => api.post(BASE, payload));

/** @returns {Promise<{share: Object}>} share.status: CREATED | OPENED | CAPTURED | EXPIRED | REVOKED */
export const getLocationShare = (shareId) => call(() => api.get(`${BASE}/${shareId}`));

/** @returns {Promise<{share: Object|null}>} latest link for a booking */
export const getBookingLocationShare = (bookingId) =>
  call(() => api.get(`${BASE}/booking/${encodeURIComponent(bookingId)}`));

export const resendLocationShare = (shareId, payload = {}) =>
  call(() => api.post(`${BASE}/${shareId}/resend`, payload));

export const revokeLocationShare = (shareId) => call(() => api.post(`${BASE}/${shareId}/revoke`));

export default {
  createLocationShare,
  getLocationShare,
  getBookingLocationShare,
  resendLocationShare,
  revokeLocationShare
};
